import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFusionAccess } from "@/lib/useFusionAccess";
import { ArrowLeft, Flame, Trophy, Sparkles, ShieldCheck, Camera, LogOut } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { money, FusionMeter, Countdown, ShareControls, brandOf, textOn, visibleOnDark } from "@/components/fusion/FusionShared";
import { FusionVideoPlayer } from "@/components/fusion/FusionVideoPlayer";

const SCRIPT_MODES = [
  { v: "standard", label: "Standard", sub: "Short and direct" },
  { v: "team_spirit", label: "Team Spirit", sub: "Energetic, school-focused" },
  { v: "performance", label: "Performance", sub: "Combine-focused" },
  { v: "personal", label: "Personal", sub: "Write your own message" },
];

export default function PlayerFusion() {
  const [data, setData] = useState(null);
  const access = useFusionAccess();
  const { logout } = useAuth();
  const nav = useNavigate();
  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };
  const load = () => api.get("/fusion/my-campaign").then((r) => setData(r.data)).catch(() => setData({ active: false }));
  useEffect(() => { load(); }, []);

  if (!data) return <Shell><div className="text-white/40 text-center pt-24 animate-pulse">Loading…</div></Shell>;
  if (!data.active) {
    return (
      <Shell>
        <div className="max-w-xl mx-auto px-5 pt-20 text-center" data-testid="fusion-inactive">
          <Flame className="h-10 w-10 mx-auto text-cyan-400" />
          <h1 className="text-2xl font-black text-white mt-4">No active Fusion campaign</h1>
          <p className="text-sm text-white/50 mt-2">When your coach activates a BioHacker Fusion™ event, your personal fundraiser page appears here.</p>
          <p className="text-xs text-white/40 mt-3">Joined through your coach's invite link but still seeing this? Sign in with the same email you used on that link, or open the link again and enter your account password — it will connect you to the event.</p>
          <Link to="/portal" className="inline-block mt-6 text-sm font-bold text-cyan-400 hover:underline">← Back to portal</Link>
        </div>
      </Shell>
    );
  }

  const { campaign, event, pledges } = data;
  const raw = brandOf(event);
  // Sanitize brand colors so near-black school colors stay readable on the dark UI
  const b = {
    ...raw,
    secondary: visibleOnDark(raw.secondary),
    accent: visibleOnDark(raw.accent),
    onSecondary: textOn(visibleOnDark(raw.secondary)),
    onAccent: textOn(visibleOnDark(raw.accent)),
  };
  const url = `${window.location.origin}/fusion/a/${campaign.slug}`;

  return (
    <Shell primary={b.primary}>
      <div className="max-w-2xl mx-auto px-5 pb-24">
        <header className="pt-6 flex items-center justify-between">
          {access.biohacker_enabled ? (
            <Link to="/portal" className="inline-flex items-center gap-1.5 text-xs font-bold text-white/50 hover:text-white" data-testid="back-to-portal-link">
              <ArrowLeft className="h-4 w-4" /> Portal
            </Link>
          ) : (
            <button onClick={handleLogout} className="inline-flex items-center gap-1.5 text-xs font-bold text-white/50 hover:text-white" data-testid="fusion-logout-btn">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          )}
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: b.secondary }}>My Fusion Dashboard</div>
        </header>

        <a href="/player-guide" target="_blank" rel="noreferrer" className="inline-block mt-3 text-[11px] font-bold underline text-white/40 hover:text-white/70" data-testid="fusion-guide-link">
          New here? Read the Player Guide →
        </a>

        <section className="mt-6 rounded-3xl border border-white/10 p-6" style={{ background: `linear-gradient(135deg, ${b.primary}dd 0%, rgba(10,18,36,0.95) 70%)` }} data-testid="fusion-athlete-dashboard">
          <div className="flex items-center gap-3">
            {b.logo && <img src={b.logo} alt={b.school} className="h-10 w-10 rounded-lg bg-white/90 p-1 object-contain" />}
            <div>
              <div className="font-black text-white">{event.name}</div>
              <div className="text-xs text-white/50">{b.school} · {event.event_date ? new Date(event.event_date).toLocaleDateString() : "date TBA"}</div>
            </div>
          </div>
          <div className="mt-5"><FusionMeter raised={campaign.raised_usd} goal={campaign.goal_usd} color={b.secondary} /></div>
          <div className="mt-3 flex gap-4 text-xs text-white/60">
            <span>{campaign.sponsor_count} sponsors</span>
            {campaign.committed_per_ft_usd > 0 && <span>${campaign.committed_per_ft_usd.toFixed(2)}/ft riding on your swings</span>}
          </div>
          <div className="mt-4"><Countdown dateStr={event.event_date} color={b.secondary} /></div>
        </section>

        {campaign.result_verified && (
          <section className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="my-result-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-amber-400"><Trophy className="h-4 w-4" /> Your verified result</div>
            <div className="text-4xl font-black text-white mt-1">{campaign.best_distance_ft?.toFixed(0)} ft</div>
          </section>
        )}

        {/* Photo upload + approval */}
        <PhotoApproval campaign={campaign} onChange={load} color={b.secondary} onColor={b.onSecondary} />

        {/* AI video workflow */}
        <VideoWorkflow campaign={campaign} event={event} brand={b} onChange={load} />

        {/* Share */}
        <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 mb-3">Share your campaign</div>
          <ShareControls url={url} title={`Support me in the BioHacker Fusion Hit-A-Thon!`} color={b.secondary}
            onShare={() => api.post(`/fusion/public/athlete/${campaign.slug}/video-event?kind=share`).catch(() => {})} />
          <a href={url} target="_blank" rel="noreferrer" className="inline-block mt-3 text-xs font-bold hover:underline" style={{ color: b.secondary }} data-testid="view-my-page-link">
            View my public page →
          </a>
        </section>

        {/* My sponsors */}
        {pledges?.length > 0 && (
          <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="my-sponsors-list">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 mb-3">My sponsors</div>
            <div className="space-y-2">
              {pledges.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-white/80 font-semibold">{p.sponsor_first} {p.sponsor_last?.[0]}.</span>
                  <span className="text-xs text-white/50">
                    {p.type === "flat" ? money(p.amount_paid_usd || p.flat_amount_usd) : `$${(p.rate_usd_per_ft || 0).toFixed(2)}/ft`}
                    <span className={`ml-2 font-black uppercase text-[9px] ${p.status === "paid" ? "text-emerald-400" : p.status === "invoiced" ? "text-amber-400" : "text-cyan-400"}`}>{p.status}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </Shell>
  );
}

function PhotoApproval({ campaign, onChange, color, onColor }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const downsample = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (ev) => { img.src = ev.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const TARGET = 640;
      const c = document.createElement("canvas");
      c.width = TARGET; c.height = TARGET;
      const ctx = c.getContext("2d");
      const ratio = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - ratio) / 2, (img.height - ratio) / 2, ratio, ratio, 0, 0, TARGET, TARGET);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Pick an image file");
    setBusy(true);
    try {
      const dataUri = await downsample(file);
      await api.post("/players/me/avatar", { avatar_url: dataUri });
      setPreview(dataUri);
      if (campaign.photo_approved) {
        await api.patch("/fusion/my-campaign", { photo_approved: true }); // refresh approved photo
        toast.success("Photo updated on your public page");
      } else {
        toast.success("Photo uploaded — tap Approve to show it on your public page");
      }
      onChange();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.patch("/fusion/my-campaign", { photo_approved: true });
      toast.success("Profile photo approved for your Fusion page");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const shown = preview || campaign.photo_url;
  return (
    <section className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="photo-approval-card">
      <div className="flex flex-wrap items-center gap-3">
        {shown ? (
          <img src={shown} alt="me" className="h-14 w-14 rounded-xl object-cover" data-testid="fusion-photo-preview" />
        ) : (
          <div className="h-14 w-14 rounded-xl bg-white/10" />
        )}
        <div className="flex-1 min-w-[180px]">
          <div className="font-bold text-white text-sm">Profile photo on your public page</div>
          <div className="text-xs text-white/50">
            {campaign.photo_approved ? "Approved — visible to supporters" : shown ? "Uploaded — approve it to show it to supporters" : "Upload a photo — your page shows initials until you do"}
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => fileRef.current?.click()} disabled={busy} size="sm" variant="outline"
            className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="upload-photo-btn">
            <Camera className="h-4 w-4 mr-1.5" /> {shown ? "Change photo" : "Upload photo"}
          </Button>
          {!campaign.photo_approved && shown && (
            <Button onClick={approve} disabled={busy} size="sm" className="font-bold" style={{ background: color, color: onColor }} data-testid="approve-photo-btn">
              Approve photo
            </Button>
          )}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="photo-file-input" />
    </section>
  );
}

const FEMALE_DEFAULT_SPORTS = ["softball", "volleyball", "girls", "women", "cheer", "gymnastics", "field hockey"];

function VideoWorkflow({ campaign, event, brand, onChange }) {
  const v = campaign.video || { status: "none" };
  const sportDefaultsFemale = FEMALE_DEFAULT_SPORTS.some((s) => (event?.sport || "").toLowerCase().includes(s));
  const [mode, setMode] = useState("standard");
  const [style, setStyle] = useState("voiceover");
  const [voice, setVoice] = useState(sportDefaultsFemale ? "nova" : "onyx");
  const [script, setScript] = useState("");
  const [isMinor, setIsMinor] = useState(false);
  const [guardian, setGuardian] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const loadScript = (m) => {
    if (m === "personal") { setScript(""); return; }
    api.get(`/fusion/my-campaign/video/script?mode=${m}`)
      .then((r) => setScript(r.data.script || ""))
      .catch(() => {});
  };
  const pickMode = (m) => { setMode(m); loadScript(m); };
  useEffect(() => {
    if (v.status === "consented" || v.status === "failed") loadScript(mode);
  }, [v.status]); // eslint-disable-line

  useEffect(() => {
    if (v.status !== "generating") return;
    const id = setInterval(onChange, 6000);
    return () => clearInterval(id);
  }, [v.status]); // eslint-disable-line

  const consent = async () => {
    setBusy(true);
    try {
      await api.post("/fusion/my-campaign/video/consent", { consent: true, is_minor: isMinor, guardian_name: guardian || null, guardian_relation: isMinor ? "parent_guardian" : null });
      toast.success("Consent recorded");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const generate = async () => {
    if (!script.trim()) return toast.error("Write or pick your video message first");
    setBusy(true);
    try {
      const { data } = await api.post("/fusion/my-campaign/video/generate", { script_mode: mode, custom_script: script, video_style: style, voice });
      setEditing(false);
      toast.success(data.video_status === "generating"
        ? "Talking-head video is generating — this takes a few minutes"
        : "AI video generated — preview it below");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try {
      await api.post("/fusion/my-campaign/video/approve");
      toast.success("Video published to your fundraiser page!");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const generatorUI = (
    <div className="mt-4 space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {[
          { v: "voiceover", label: "Voiceover + Motion", sub: "Your photo with motion + AI voice · ready in seconds" },
          { v: "talking_head", label: "Talking Head", sub: "AI makes your photo speak the ask · takes a few minutes" },
        ].map((s) => (
          <button key={s.v} onClick={() => setStyle(s.v)} data-testid={`video-style-${s.v}`}
            className={`text-left p-3 rounded-xl border text-xs ${style === s.v ? "border-transparent" : "border-white/15 text-white/70 bg-white/5"}`}
            style={style === s.v ? { background: brand.accent, color: brand.onAccent } : {}}>
            <div className="font-black">{s.label}</div>
            <div className="opacity-70">{s.sub}</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {SCRIPT_MODES.map((m) => (
          <button key={m.v} onClick={() => pickMode(m.v)} data-testid={`script-mode-${m.v}`}
            className={`text-left p-3 rounded-xl border text-xs ${mode === m.v ? "border-transparent" : "border-white/15 text-white/70 bg-white/5"}`}
            style={mode === m.v ? { background: brand.secondary, color: brand.onSecondary } : {}}>
            <div className="font-black">{m.label}</div>
            <div className="opacity-70">{m.sub}</div>
          </button>
        ))}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Voice</div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { v: "onyx", label: "Male voice" },
            { v: "nova", label: "Female voice" },
          ].map((o) => (
            <button key={o.v} onClick={() => setVoice(o.v)} data-testid={`voice-${o.v === "onyx" ? "male" : "female"}`}
              className={`p-2.5 rounded-xl border text-xs font-black ${voice === o.v ? "border-transparent" : "border-white/15 text-white/70 bg-white/5"}`}
              style={voice === o.v ? { background: brand.secondary, color: brand.onSecondary } : {}}>
              {o.label}{((o.v === "nova") === sportDefaultsFemale) && <span className="font-normal opacity-70"> · suggested for {event?.sport || "your sport"}</span>}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Your video message — edit the wording however you like</div>
        <Textarea value={script} onChange={(e) => setScript(e.target.value)} maxLength={900} rows={5}
          placeholder={mode === "personal" ? "Write your own message (approved fundraising content only)" : "Loading script…"}
          className="bg-white/5 border-white/15 text-white" data-testid="video-script-input" />
        <div className="flex justify-between text-[10px] text-white/35 mt-1">
          <span>This exact wording becomes the video's voice.</span>
          <span>{script.length}/900</span>
        </div>
      </div>
      <Button onClick={generate} disabled={busy || !campaign.photo_approved} size="sm" className="font-bold" style={{ background: brand.secondary, color: brand.onSecondary }} data-testid="video-generate-btn">
        {busy ? "Generating…" : "Generate my video"}
      </Button>
      {!campaign.photo_approved && <div className="text-[11px] text-amber-400">Approve your profile photo above first.</div>}
    </div>
  );

  return (
    <section className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="video-workflow-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: brand.secondary }}>
        <Sparkles className="h-4 w-4" /> AI Fundraising Video
      </div>
      <p className="text-xs text-white/50 mt-1.5">
        AI generates your pledge ask — a voiceover-with-motion video, or a talking-head video where your photo appears to speak. Nothing is published without your approval.
      </p>

      {v.status === "none" && (
        <div className="mt-4 space-y-3">
          <div className="flex items-start gap-2 text-xs text-white/60">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brand.secondary }} />
            <span>By consenting you acknowledge AI technology will generate a voice + video representation using your approved profile photo and campaign details. You can revoke anytime.</span>
          </div>
          <label className="flex items-center gap-2 text-xs text-white/70">
            <Checkbox checked={isMinor} onCheckedChange={setIsMinor} data-testid="minor-checkbox" /> I am under 18 (parent/guardian consent required)
          </label>
          {isMinor && (
            <Input placeholder="Parent/guardian full name" value={guardian} onChange={(e) => setGuardian(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="guardian-name-input" />
          )}
          <Button onClick={consent} disabled={busy || (isMinor && !guardian.trim())} size="sm" className="font-bold" style={{ background: brand.secondary, color: brand.onSecondary }} data-testid="video-consent-btn">
            Grant consent
          </Button>
        </div>
      )}

      {v.status === "consented" && generatorUI}

      {v.status === "generating" && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="video-generating-state">
          <div className="h-5 w-5 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          <div className="text-xs text-white/70">
            <div className="font-black text-white">Creating your talking-head video…</div>
            This usually takes 2–5 minutes. You can leave this page — it'll be waiting in preview.
          </div>
        </div>
      )}

      {v.status === "failed" && (
        <div className="mt-4 space-y-3">
          <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-xs text-red-300" data-testid="video-failed-state">
            Video generation failed{v.error ? ` — ${v.error}` : ""}. Try again (Voiceover + Motion always works instantly).
          </div>
          {generatorUI}
        </div>
      )}

      {(v.status === "preview" || v.status === "published") && v.asset_id && (
        <div className="mt-4 space-y-3">
          <FusionVideoPlayer photoUrl={campaign.photo_url} athleteName={campaign.athlete_name}
            school={brand.school} logo={brand.logo} color={brand.secondary}
            assetId={v.asset_id} script={v.script} style={v.style} />
          {v.status === "preview" ? (
            <div className="flex gap-2 flex-wrap">
              <Button onClick={approve} disabled={busy} size="sm" className="font-bold text-[#0b1424] bg-emerald-400 hover:bg-emerald-300" data-testid="video-approve-btn">
                Approve & publish
              </Button>
              <Button onClick={() => { setScript(v.script || ""); setEditing((x) => !x); }} disabled={busy} size="sm" variant="outline" className="border-white/20 text-white" data-testid="video-edit-wording-btn">
                {editing ? "Hide editor" : "Edit wording & regenerate"}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs font-bold text-emerald-400" data-testid="video-published-badge">✓ Published on your fundraiser page · {v.analytics?.views || 0} views · {v.analytics?.shares || 0} shares</div>
              <Button onClick={() => { setScript(v.script || ""); setEditing((x) => !x); }} disabled={busy} size="sm" variant="outline" className="border-white/20 text-white text-xs" data-testid="video-new-version-btn">
                {editing ? "Hide editor" : "Edit wording & create new version"}
              </Button>
            </div>
          )}
          {editing && generatorUI}
        </div>
      )}
    </section>
  );
}

function Shell({ children, primary = "#0B1E36" }) {
  return (
    <div className="min-h-screen" style={{ background: `radial-gradient(1200px 500px at 50% -100px, ${primary}66, transparent), #080e1c` }}>
      {children}
    </div>
  );
}
